import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Integrations from "@/pages/Integrations";
import DashboardLayout from "@/components/DashboardLayout";
import MerchantOverview from "@/pages/dashboard/MerchantOverview";
import MerchantApply from "@/pages/dashboard/MerchantApply";
import Transactions from "@/pages/dashboard/Transactions";
import NewTransaction from "@/pages/dashboard/NewTransaction";
import Chargebacks from "@/pages/dashboard/Chargebacks";
import CreditApplication from "@/pages/dashboard/CreditApplication";
import Gateway from "@/pages/dashboard/Gateway";
import Billing from "@/pages/dashboard/Billing";
import PaymentReturn from "@/pages/PaymentReturn";
import AdminDashboard from "@/pages/dashboard/AdminDashboard";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] text-slate-900" data-testid="auth-loading">
        <div className="font-mono text-sm text-slate-500">Verifying session...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/integrations" element={<Integrations />} />
      <Route path="/payment-return" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<MerchantOverview />} />
        <Route path="apply" element={<MerchantApply />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="transactions/new" element={<NewTransaction />} />
        <Route path="chargebacks" element={<Chargebacks />} />
        <Route path="credit" element={<CreditApplication />} />
        <Route path="gateway" element={<Gateway />} />
        <Route path="billing" element={<Billing />} />
        <Route path="admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="light" position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
